<template>
    <div class="navbar-layout-container">
        <!-- Esenda logo/Hubpay logo -->
        <div class="navbar-logo-container">
            <!-- TODO update hubpay logo -->
            <v-img
                v-if="pStore.getIsHubpay"
                class="navbar-logo-hubpay-img"
                :src="getImageUrl('hubpay.svg')"
                alt="Hubpay logo"
            />
            <v-img
                v-else
                class="navbar-logo-esenda-img"
                :src="getImageUrl('esenda.svg')"
                alt="Esenda logo"
            />
        </div>

        <!-- Menu list -->
        <v-list
            color="transparent"
            class="navbar-menu-items-container"
            data-cy="navigation-bar"
        >
            <template v-if="showOnboardingNavigation">
                <v-list-item
                    color="white"
                    :to="{
                        name: OrganisationRouteNames.CUSTOMER_ONBOARDING,
                        query: { step: OnboardingStage.WELCOME },
                    }"
                    title="Welcome to esenda"
                    :active="activeOnboardingStep === OnboardingStage.WELCOME"
                    class="mb-2"
                    data-cy="onboardingWelcome"
                />
                <v-list-item
                    v-for="(step, index) in onboardingSteps"
                    :key="step.id"
                    color="white"
                    :to="{
                        name: OrganisationRouteNames.CUSTOMER_ONBOARDING,
                        query: { step: step.id },
                    }"
                    :disabled="
                        onboardingStages.indexOf(step.id) >
                        onboardingStages.indexOf(onboardingStore.stage)
                    "
                    :active="activeOnboardingStep === step.id"
                    :aria-current="
                        activeOnboardingStep === step.id ? 'step' : undefined
                    "
                    :data-cy="`onboardingStep-${step.id}`"
                    class="onboarding-menu-item"
                >
                    <template #prepend>
                        <v-icon
                            v-if="
                                onboardingStages.indexOf(step.id) <
                                onboardingStages.indexOf(onboardingStore.stage)
                            "
                            :icon="mdiCheckCircleOutline"
                            color="success"
                            class="mr-3"
                        />
                        <span v-else class="onboarding-step-number mr-3">{{
                            index + 1
                        }}</span>
                    </template>
                    <span class="body-medium">{{ step.title }}</span>
                </v-list-item>
                <v-divider class="my-4" />
            </template>
            <v-list-item v-for="(item, i) in filteredRoutes" :key="i">
                <div
                    :class="{
                        'navbar-menu-item-active': isParentItemActive(item),
                        'navbar-menu-item': !isParentItemActive(item),
                    }"
                    @click="
                        item.subitems
                            ? toggleSubItems(i as number)
                            : handleClick(item.route)
                    "
                >
                    <v-icon
                        :class="{
                            'navbar-item-icon-active': isParentItemActive(item),
                            'navbar-item-icon': !isParentItemActive(item),
                        }"
                        :icon="item.iconPath"
                    />
                    <span class="navbar-menu-item-text h200">{{
                        item.title
                    }}</span>
                    <v-icon
                        v-if="item.subitems"
                        :class="{
                            'navbar-item-icon-arrow-expanded':
                                isSubmenuExpanded && expandedIndex === i,
                            'navbar-item-icon-arrow-collapsed':
                                !isSubmenuExpanded || expandedIndex !== i,
                        }"
                        :icon="mdiChevronDown"
                    />
                </div>
                <!-- Subitems -->
                <v-list
                    v-if="
                        item.subitems &&
                        isSubmenuExpanded &&
                        expandedIndex === i
                    "
                    color="transparent"
                >
                    <v-list-item v-for="(subitem, j) in item.subitems" :key="j">
                        <div
                            :class="{
                                'navbar-submenu-item-active':
                                    isSubItemActive(subitem),
                                'navbar-submenu-item':
                                    !isSubItemActive(subitem),
                            }"
                            @click="handleClick(subitem.route)"
                        >
                            <v-icon
                                :icon="subitem.iconPath"
                                :class="{
                                    'navbar-submenu-item-icon-active':
                                        isSubItemActive(subitem),
                                    'navbar-submenu-item-icon':
                                        !isSubItemActive(subitem),
                                }"
                            />
                            <span class="navbar-menu-item-text body-small">
                                {{ subitem.title }}
                            </span>
                        </div>
                    </v-list-item>
                </v-list>
            </v-list-item>
        </v-list>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { usePiniaStore } from "@/stores/store";
import { useOrganisationStore } from "@/stores/organisationStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import {
    onboardingSteps,
    onboardingStages,
    OnboardingStage,
} from "@/constants/onboarding";
import { signOut } from "aws-amplify/auth";

import { navSchoolRoutes, filterNavRoutes } from "@/methods/navbarRoutes";
import { getImageUrl } from "@/methods/iconHelpers";

import type { NavItem } from "@/types/routes";
import { redirectToRoute } from "@/router";
import { hasRequiredPermissions } from "@/methods/permissionsHelpers";
import { AuthRouteNames, OrganisationRouteNames } from "@/constants/routes";
import { mdiChevronDown, mdiCheckCircleOutline } from "@mdi/js";

const pStore = usePiniaStore();
const organisationStore = useOrganisationStore();
const onboardingStore = useOnboardingStore();
const router = useRouter();
const route = useRoute();
const showOnboardingNavigation = computed(
    () =>
        onboardingStore.required ||
        (route.name === OrganisationRouteNames.CUSTOMER_ONBOARDING &&
            onboardingStore.inProgress),
);
const activeOnboardingStep = computed(() =>
    route.name === OrganisationRouteNames.CUSTOMER_ONBOARDING
        ? onboardingStore.resolveStage(route.query.step)
        : null,
);

const expandedIndex = ref<number | null>(null);
const isSubmenuExpanded = ref(false);

const userHasPermissions = computed(
    () => Object.keys(pStore.getPermissions ?? {}).length > 0,
);

/**
 * Filters the navigation routes based on the user's permissions and organisation products.
 * Memoized to avoid unnecessary recalculations on re-renders.
 * If the user doesn't have any permissions, returns an empty array to hide the
 * navbar until permissions are detected.
 */
const filteredRoutes = computed((): NavItem[] => {
    if (!userHasPermissions.value) return [];
    if (showOnboardingNavigation.value) {
        return navSchoolRoutes.filter(
            ({ id }) =>
                id === "support" ||
                id === "logout" ||
                (id === "dashboard" && !onboardingStore.required),
        );
    }
    return filterNavRoutes(navSchoolRoutes, {
        orgProducts: pStore.getOrganisationProducts,
        hasPermission: (perms) => hasRequiredPermissions(perms),
        contractsSigned: pStore.areContractsSigned,
    });
});

const path = computed(() => route.path);

const handleClick = (path: string) => {
    if (path === "logout") {
        handleLogout();
    } else {
        changeRoute(path);
    }
};

const changeRoute = (path: string) => {
    router.push(path).catch((err) => {
        console.log("Route error: " + err);
    });
};

const toggleSubItems = (index: number) => {
    expandedIndex.value = expandedIndex.value === index ? null : index;
    isSubmenuExpanded.value = expandedIndex.value !== null;
};

/**
 * Checks if the current path starts with the base path. Used to determine if a nav item should be active.
 * @param {string} current The current path
 * @param {string} base The base path to check against
 * @returns {boolean} True if the current path is the same as the base or starts with the base followed by a slash, false otherwise
 */
const pathStartsWith = (current: string, base: string): boolean =>
    current === base || current.startsWith(base + "/");

/**
 * Checks if a parent nav item should be marked as active. A parent item is active
 * if the current path matches its route or any of its subitems' routes.
 * @param {NavItem} item The parent nav item to check
 * @returns {boolean} True if the parent item is active, false otherwise
 */
const isParentItemActive = (item: NavItem): boolean =>
    pathStartsWith(path.value, item.route) ||
    (item.subitems?.some((sub) => pathStartsWith(path.value, sub.route)) ??
        false);

/**
 * Checks if a subitem should be marked as active. A subitem is active if the
 * current path matches its route.
 * @param {NavItem} subitem The subitem to check
 * @returns {boolean} True if the subitem is active, false otherwise
 */
const isSubItemActive = (subitem: NavItem): boolean =>
    pathStartsWith(path.value, subitem.route);

const handleLogout = async () => {
    pStore.setIsAuthenticated(false);
    try {
        await signOut({ global: true });
    } finally {
        organisationStore.clearOrganisationContext(pStore.getUserEmail);
        redirectToRoute({ name: AuthRouteNames.LOGIN });
    }
};

// Ensure submenu is expanded when component mounts if the current route is a subitem route
onMounted(async () => {
    setTimeout(() => {
        filteredRoutes.value.forEach((item, index) => {
            if (
                item.subitems &&
                item.subitems.some((subitem) => subitem.route === path.value)
            ) {
                expandedIndex.value = index;
                isSubmenuExpanded.value = true;
            }
        });
    }, 1000);
});

watch(path, () => {
    // Close submenu if the current path doesn't match any of the submenu routes
    if (
        expandedIndex.value !== null &&
        filteredRoutes.value[expandedIndex.value] &&
        !isParentItemActive(filteredRoutes.value[expandedIndex.value])
    ) {
        expandedIndex.value = null;
    }
});
</script>

<style lang="css" scoped>
.v-list {
    padding: 0 !important;
}

.onboarding-menu-item {
    min-height: 56px;
    white-space: normal;
}

.onboarding-menu-item :deep(.v-list-item__prepend > .v-list-item__spacer) {
    width: 0;
}

.onboarding-step-number {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border: 1px solid currentColor;
    border-radius: 50%;
}
</style>
